import type { ChartHighlightPoint } from '../../types';
import type { CompetitorChartSeries, ShareType } from '../../types';
import { formatCpi, formatPercent, interpolateYAtX, svgBlue, svgValue } from './chartHelpers';
import competitividadeIcon from '../../../../../../assets/elasticidade-cruzada/Competitividade.svg';
import styles from './styles.module.scss';

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const TOOLTIP_NAME_MAX_SINGLE_LINE_LENGTH = 35;
const TOOLTIP_NAME_MIN_LINES = 6;

type TooltipCompetitorNameFormat = {
  html: string;
  isMultiline: boolean;
};

const toMultilineResult = (lines: string[]): TooltipCompetitorNameFormat => ({
  html: lines.map(escapeHtml).join('<br/>'),
  isMultiline: true,
});

const chunkWords = (words: string[], lineCount: number): string[] => {
  const wordsPerLine = Math.ceil(words.length / lineCount);
  const lines: string[] = [];

  for (let index = 0; index < words.length; index += wordsPerLine) {
    lines.push(words.slice(index, index + wordsPerLine).join(' '));
  }

  return lines;
};

// Tooltip de nome de competidor, quando for muito longo, quebra em várias linhas
// Média de competidores, quando o filtro de concorrente for TODOS
export const formatTooltipCompetitorName = (name: string): TooltipCompetitorNameFormat => {
  if (name.length <= TOOLTIP_NAME_MAX_SINGLE_LINE_LENGTH) {
    return { html: `${escapeHtml(name)}:`, isMultiline: false };
  }

  const averageMatch = name.match(/^MÉDIA \((.+)\)$/);
  if (averageMatch) {
    const names = averageMatch[1]
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);

    return toMultilineResult([
      'MÉDIA (',
      ...names.map((competitorName, index) => (index === names.length - 1 ? `${competitorName})` : `${competitorName},`)),
    ]);
  }

  return toMultilineResult(chunkWords(name.split(/\s+/), TOOLTIP_NAME_MIN_LINES));
};

const buildMultilineCompetitorContent = (nameHtml: string, share: number) => {
  const lines = nameHtml.split('<br/>');
  const lastLine = lines.pop() ?? '';
  const shareFormatted = formatPercent(share, 1);
  const leadingLines = lines.join('<br/>');

  return `
    ${leadingLines ? `${leadingLines}<br/>` : ''}
    <span class="${styles.tooltipCompetitorsLastLine}">
      <span class="${styles.tooltipCompetitorsLastLineLabel}">${lastLine}:</span>
      <b class="${styles.tooltipCompetitorsValue}">${shareFormatted}</b>
    </span>
  `;
};

const TOOLTIP_TITLE_MAP: Record<ChartHighlightPoint['kind'], string> = {
  bullet: 'Competitividade:',
  current: 'Competitividade atual Infopanel:',
  // optimal: 'Competitividade ótima:',
  optimal: 'Competitividade:',
};

const TOOLTIP_LABELS_MAP: Record<ChartHighlightPoint['kind'], { value: string; volume: string }> = {
  bullet: {
    value: 'Projeção de Market Share valor:',
    volume: 'Projeção de Market Share volume:',
  },
  current: {
    value: 'Projeção de Market Share valor:',
    volume: 'Projeção de Market Share volume:',
  },
  optimal: {
    value: 'Projeção de Market Share ótimo valor:',
    volume: 'Projeção de Market Share volume:',
  },
};

// Tooltip quando não tem competidores
export const buildTooltipContent = (point: ChartHighlightPoint) => {
  const titleIcon = `<span class="${styles.tooltipTitleIcon}"><img src="${competitividadeIcon}" alt="Competitividade" width="12" height="12" /></span>`;
  const titleLabel = TOOLTIP_TITLE_MAP[point.kind];
  const { value: valueLabel, volume: volumeLabel } = TOOLTIP_LABELS_MAP[point.kind];
  const titleValue = `<b class="${styles.tooltipTitleValue}">${formatCpi(point.x)}</b>`;

  return `
    <div class="${styles.tooltip}">
      <div class="${styles.tooltipTitle}">
        ${titleIcon}
        <span class="${styles.tooltipTitleText}">${titleLabel}</span>
        ${titleValue}
      </div>
      <div class="${styles.tooltipRow}">
        ${svgBlue}
        <span>${volumeLabel} <b>${formatPercent(point.volumeShare, 1)}</b></span>
      </div>
      <div class="${styles.tooltipRow}">
        ${svgValue}
        <span>${valueLabel} <b>${formatPercent(point.valueShare, 1)}</b></span>
      </div>
      ${
        point.kind === 'optimal' && point.gain !== null && point.gain !== undefined
          ? `<div class="${styles.tooltipGain}"><b>${formatPercent(point.gain, 1).replace('%', ' p.p.')}</b> de market share em potencial de ganho</div>`
          : ''
      }
    </div>
  `;
};

export const buildTooltipContentCompetitor = (
  point: ChartHighlightPoint,
  curvesClient?: string | null,
  visibleCompetitorSeries?: CompetitorChartSeries[] | null,
  competitorDisplayMode?: ShareType | null,
) => {
  const titleIcon = `<span class="${styles.tooltipTitleIcon}"><img src="${competitividadeIcon}" alt="Competitividade" width="12" height="12" /></span>`;
  const titleLabel = TOOLTIP_TITLE_MAP[point.kind];
  const titleValue = `<b class="${styles.tooltipTitleValue}">${formatCpi(point.x)}</b>`;
  const clientName = curvesClient?.trim() ? escapeHtml(curvesClient.trim()) : 'Cliente';

  const buildCompetitorBulletIcon = (color: string) => `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 8H14" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      <circle cx="8" cy="8" r="3" fill="${color}" stroke="white" stroke-width="1.5" />
    </svg>
  `;

  const buildCompetitorRanking = (competitorSeries: CompetitorChartSeries[]) =>
    competitorSeries
      .map(comp => {
        const share = interpolateYAtX(comp.series.data, point.x);
        return typeof share === 'number' && !Number.isNaN(share) ? { name: comp.competitorName, share, color: comp.series.color } : null;
      })
      .filter((value): value is { name: string; share: number; color: string } => value !== null)
      .sort((first, second) => second.share - first.share);

  const selectedCompetitors = buildCompetitorRanking(visibleCompetitorSeries ?? []);
  const selectedMode = competitorDisplayMode ?? 'VALOR';
  const selectedModeClientShare = selectedMode === 'VALOR' ? point.valueShare : point.volumeShare;
  const nonSelectedModeClientShare = selectedMode === 'VALOR' ? point.volumeShare : point.valueShare;
  const selectedModeIcon = selectedMode === 'VALOR' ? svgValue : svgBlue;
  const nonSelectedModeIcon = selectedMode === 'VALOR' ? svgBlue : svgValue;
  const nonSelectedModeLabel = selectedMode === 'VALOR' ? 'volume' : 'valor';
  const selectedModeLabel = point.kind === 'optimal' && selectedMode === 'VALOR' ? 'ótimo valor' : selectedMode === 'VALOR' ? 'valor' : 'volume';

  const competitorsBlockHtml = `
    <div class="${styles.tooltipCompetitorsList}">
      <div class="${styles.tooltipCompetitorsRow}">
        <span class="${styles.tooltipCompetitorsName}">
          <span class="${styles.tooltipCompetitorsIcon}">${selectedModeIcon}</span>
          ${clientName}:
        </span>
        <b class="${styles.tooltipCompetitorsValue}">${formatPercent(selectedModeClientShare, 1)}</b>
      </div>
      ${selectedCompetitors
        .map(item => {
          const { html: nameHtml, isMultiline } = formatTooltipCompetitorName(item.name);
          const rowClass = isMultiline ? `${styles.tooltipCompetitorsRow} ${styles.tooltipCompetitorsRowMultiline}` : styles.tooltipCompetitorsRow;

          if (isMultiline) {
            return `
              <div class="${rowClass}">
                <span class="${styles.tooltipCompetitorsIcon}">${buildCompetitorBulletIcon(item.color)}</span>
                <span class="${styles.tooltipCompetitorsNameText}">${buildMultilineCompetitorContent(nameHtml, item.share)}</span>
              </div>
            `;
          }

          return `
            <div class="${rowClass}">
              <span class="${styles.tooltipCompetitorsName}">
                <span class="${styles.tooltipCompetitorsIcon}">${buildCompetitorBulletIcon(item.color)}</span>
                ${nameHtml}
              </span>
              <b class="${styles.tooltipCompetitorsValue}">${formatPercent(item.share, 1)}</b>
            </div>
          `;
        })
        .join('')}
    </div>
  `;

  return `
    <div class="${styles.tooltip}">
      <div class="${styles.tooltipTitle}">
        ${titleIcon}
        <span class="${styles.tooltipTitleText}">${titleLabel}</span>
        ${titleValue}
      </div>
      <div class="${styles.tooltipTitle}">
        <span class="${styles.projectionText}">Projeção de Market Share ${nonSelectedModeLabel}</span>
      </div>
      <div class="${styles.tooltipRow}">
        ${nonSelectedModeIcon}
        <span>${clientName}: <b>${formatPercent(nonSelectedModeClientShare, 1)}</b></span>
      </div>
      <div class="${styles.tooltipTitle}">
        <span class="${styles.projectionText}">Projeção de Market Share ${selectedModeLabel}</span>
      </div>
      <div class="${styles.tooltipCompetitorsBlock}">${competitorsBlockHtml}</div>
      ${
        point.kind === 'optimal' && point.gain !== null && point.gain !== undefined
          ? `<div class="${styles.tooltipGain}"><b>${formatPercent(point.gain, 1).replace('%', ' p.p.')}</b> de market share em potencial de ganho</div>`
          : ''
      }
    </div>
  `;
};
